import { decodeBase64Url, encodeBase64Url } from "@/helpers/UrlStateHelpers";
import { SortingState, TableState } from "@tanstack/react-table";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

export const TABLE_STATE_URL_PARAM = "f";

//* Clés propres à TanStack Table : tout le reste de l'état d'un tableau est un filtre.
//* `sorting` n'y figure pas, le tri est conservé avec les filtres.
const TANSTACK_STATE_KEYS = [
  "columnVisibility",
  "columnOrder",
  "columnPinning",
  "rowPinning",
  "columnFilters",
  "globalFilter",
  "expanded",
  "grouping",
  "columnSizing",
  "columnSizingInfo",
  "pagination",
  "rowSelection",
];

type PersistedState = Record<string, unknown>;

const MAX_STRING_LENGTH = 200;
const MAX_DEPTH = 2;

const isBlank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

//* Les options des selects embarquent l'objet API entier (notes, relations…) :
//* on n'en garde que les champs courts, de quoi réafficher leur libellé.
const slimRecord = (
  record: Record<string, unknown>,
  depth: number,
): Record<string, unknown> => {
  const slim: Record<string, unknown> = {};
  Object.entries(record).forEach(([key, value]) => {
    if (typeof value === "string") {
      if (value.length <= MAX_STRING_LENGTH) slim[key] = value;
    } else if (
      value === null ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      slim[key] = value;
    } else if (isPlainObject(value) && depth < MAX_DEPTH) {
      slim[key] = slimRecord(value, depth + 1);
    }
  });
  return slim;
};

const slimFilterValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => (isPlainObject(item) ? slimRecord(item, 1) : item));
  }
  return isPlainObject(value) ? slimRecord(value, 1) : value;
};

/**
 * Ce qui distingue l'état d'un tableau de ses valeurs par défaut : filtres et tri.
 * Un filtre par défaut vidé par l'utilisateur est noté `null`, pour ne pas
 * réapparaître au rechargement.
 */
export const toPersistedTableState = (
  state: PersistedState,
  defaults: PersistedState,
): PersistedState => {
  const persisted: PersistedState = {};
  const keys = new Set([...Object.keys(defaults), ...Object.keys(state)]);
  keys.forEach((key) => {
    if (TANSTACK_STATE_KEYS.includes(key)) return;
    const value = state[key];
    const fallback = defaults[key];
    if (isBlank(value) && isBlank(fallback)) return;
    if (JSON.stringify(value) === JSON.stringify(fallback)) return;
    persisted[key] = isBlank(value)
      ? null
      : key === "sorting"
        ? value
        : slimFilterValue(value);
  });
  return persisted;
};

const isSortingState = (value: unknown): value is SortingState =>
  Array.isArray(value) &&
  value.every(
    (sort) =>
      isPlainObject(sort) &&
      typeof sort.id === "string" &&
      typeof sort.desc === "boolean",
  );

const parsePersistedState = (raw: string | null): PersistedState | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;
    const restored: PersistedState = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (TANSTACK_STATE_KEYS.includes(key)) return;
      if (key === "sorting" && value !== null && !isSortingState(value)) return;
      restored[key] = value === null ? undefined : value;
    });
    return restored;
  } catch {
    return null;
  }
};

const decodeUrlState = (raw: string | null): PersistedState | null => {
  if (!raw) return null;
  try {
    return parsePersistedState(decodeBase64Url(raw));
  } catch {
    return null;
  }
};

const readStoredState = (storageKey: string): PersistedState | null => {
  try {
    return parsePersistedState(localStorage.getItem(storageKey));
  } catch {
    return null;
  }
};

/**
 * État d'un tableau dont les filtres et le tri survivent au rechargement et à la
 * navigation : ils vivent dans l'URL (`?f=`, partageable), et la dernière valeur
 * connue est reprise quand on revient sur la page sans paramètre (menu, lien).
 *
 * À utiliser à la place de `useState` pour l'état passé à `<Table>`, avec le même
 * `tableName`.
 */
export const useTableState = <TState extends Partial<TableState>>(
  tableName: string,
  defaults: TState,
): [TState, Dispatch<SetStateAction<TState>>] => {
  const storageKey = `${tableName}Filters`;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDefaults = useRef(defaults).current;

  const [state, setState] = useState<TState>(() => ({
    ...initialDefaults,
    ...(decodeUrlState(searchParams.get(TABLE_STATE_URL_PARAM)) ??
      readStoredState(storageKey)),
  }));

  const persisted = useMemo(
    () =>
      toPersistedTableState(
        state as PersistedState,
        initialDefaults as PersistedState,
      ),
    [state, initialDefaults],
  );
  const hasPersisted = Object.keys(persisted).length > 0;
  const serialized = hasPersisted ? JSON.stringify(persisted) : null;
  const encoded = serialized ? encodeBase64Url(serialized) : null;
  const urlValue = searchParams.get(TABLE_STATE_URL_PARAM);

  useEffect(() => {
    try {
      if (serialized) localStorage.setItem(storageKey, serialized);
      else localStorage.removeItem(storageKey);
    } catch {
      //* Stockage indisponible (navigation privée, quota) : l'URL suffit
    }
  }, [storageKey, serialized]);

  //* Resynchronise aussi l'URL quand on revient sur la page sans paramètre
  useEffect(() => {
    if (urlValue === encoded) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (encoded) next.set(TABLE_STATE_URL_PARAM, encoded);
        else next.delete(TABLE_STATE_URL_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [encoded, urlValue, setSearchParams]);

  return [state, setState];
};
